import { UserProperty } from '@/models/Property';
import { apiClient } from '@/services/api';
import { logger } from '@/utils/logger/loggerInstance';

export interface AdminUserProfile {
  firstName?: string;
  lastName?: string;
  phoneNumber?: number;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: number;
  dreNumber?: string;
  dreExpiry?: string;
  markets?: string[];
  languages?: string[];
  linkedinUrl?: string;
  photoUrl?: string;
  bio?: string;
  yearsExperience?: string;
}

export interface AdminUser {
  userId: string;
  username: string;
  email?: string;
  name?: string;
  userStatus?: string;
  enabled: boolean;
  createdAt?: string;
  platformRole: 'SuperAdmin' | 'Admin' | 'Advisor' | 'Buyer';
  groups: string[];
  /**
   * Status for Buyer-role accounts: 'buyer' (was invited by a team member
   * -- currently on a client group/deal, or was and got removed -- and
   * hasn't closed on a property yet), 'owner' (self-serve/direct
   * registration with no invite history, or an invited buyer who has since
   * closed and now owns a property). Always null for non-Buyer roles.
   */
  buyerCategory: 'buyer' | 'owner' | null;
  /** Onboarding profile data; null if the user hasn't completed their profile. Always present in API responses — never undefined. */
  profile: AdminUserProfile | null;
}

export interface ListUsersResponse {
  users: AdminUser[];
  nextToken?: string;
}

/**
 * Fetches all platform users (Admin only)
 */
export const listUsers = async (nextToken?: string): Promise<ListUsersResponse> => {
  const url = nextToken
    ? `/admin/users?nextToken=${encodeURIComponent(nextToken)}`
    : '/admin/users';

  logger.debug(`GET ${url}`);
  const response = await apiClient.get<ListUsersResponse>(url);

  return response.data;
};

/**
 * Bounded search across given_name/family_name/email (see #1268 and
 * CognitoClientWrapper.searchUsers's own comment for the prefix-match-only
 * limitation vs the old client-side substring search). No nextToken for the
 * search case — see searchUsers's comment for why.
 */
export const searchUsers = async (search: string): Promise<ListUsersResponse> => {
  const url = `/admin/users?search=${encodeURIComponent(search)}`;

  logger.debug(`GET ${url}`);
  const response = await apiClient.get<ListUsersResponse>(url);

  return response.data;
};

export interface UpdateAdminUserProfileRequest {
  firstName?: string;
  lastName?: string;
  phoneNumber?: number | null;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: number | null;
  dreNumber?: string;
  dreExpiry?: string;
  markets?: string[];
  languages?: string[];
  linkedinUrl?: string;
  bio?: string;
  yearsExperience?: string;
}

/**
 * Update a user's profile fields (Admin only).
 * The Lambda derives the Cognito username server-side from userId so Cognito
 * name sync can never target the wrong account via a spoofed query param.
 */
export const updateAdminUserProfile = async (
  userId: string,
  request: UpdateAdminUserProfileRequest,
): Promise<void> => {
  const url = `/admin/users/${encodeURIComponent(userId)}/profile`;

  logger.debug(`PATCH ${url}`);
  await apiClient.patch(url, request);
};

/**
 * Change a user's platform role (Admin only)
 */
export const changeUserRole = async (
  username: string,
  role: 'Admin' | 'Advisor' | 'Buyer',
): Promise<void> => {
  const url = `/admin/users/${encodeURIComponent(username)}/role`;

  logger.debug(`PUT ${url}`);
  await apiClient.put(url, { role });
};

/**
 * Deactivate or re-activate a user (Admin only)
 */
export const setUserActivation = async (username: string, activate: boolean): Promise<void> => {
  const url = `/admin/users/${encodeURIComponent(username)}/deactivate`;

  logger.debug(`PATCH ${url}`);
  await apiClient.patch(url, { activate });
};

/**
 * Hard-delete a user and all their data (Admin only, CCPA compliant)
 */
export const deleteUser = async (username: string): Promise<void> => {
  const url = `/admin/users/${encodeURIComponent(username)}`;

  logger.debug(`DELETE ${url}`);
  await apiClient.delete(url);
};

// ============ PROPERTY ENDPOINTS ============

export interface AdminPropertyOwner {
  userId: string;
  email?: string;
  name?: string;
}

export interface AdminInspectionReport {
  id: string;
  propertyId: string;
  propertyAddress: string;
  userId: string;
  uploadedBy?: string;
  reportType: string;
  dateOfInspection: string | null;
  documentType?: string;
  documentTypeConfidence?: number;
  issueIds: string[];
  createdAt: number;
  updatedAt: number;
  /** DocumentRecord id for the underlying upload — enables download link. */
  documentRecordId?: string;
  /** Uploaded Document id. Missing on legacy/orphan records hidden from document lists. */
  documentId?: string;
  /** Whether this report was seller-furnished or buyer-procured. */
  source?: 'seller' | 'buyer';
}

/**
 * A document upload that halted at address verification — the classifier
 * extracted a subject address that didn't match the property (or couldn't
 * verify it), so the pipeline stopped before extraction. Surfaced in the
 * admin UI with retry / delete actions so the user can resolve it.
 */
export interface AdminPendingDocument {
  /** DocumentRecord id (used for delete + presigned-URL endpoints). */
  id: string;
  /** Document id (used by the override endpoint to resume the pipeline). */
  documentId?: string;
  name: string;
  documentType?: string;
  extractedAddress?: string;
  extractedAddressConfidence?: number;
  /** Backend-defined confidence threshold; surfaced so UI doesn't drift. */
  confidenceThreshold?: number;
  createdAt: number;
  source?: 'seller' | 'buyer';
}

/**
 * A document upload that exhausted its state-machine Retry budget on a
 * Pulse-dependent step (OCR dispatch or an HOA extractor) — most likely
 * because Pulse itself was unavailable. Distinct from AdminPendingDocument
 * (a human decision to make, e.g. address mismatch): this just needs a
 * retry once Pulse recovers. Admin/superadmin only — see PropertiesTab.tsx.
 */
export interface AdminIncompleteDocument {
  /** DocumentRecord id (used by the retry endpoint). */
  id: string;
  /** Document id (used by the retry endpoint to resume the pipeline). */
  documentId?: string;
  name: string;
  documentType?: string;
  processingIncompleteReason?: string;
  /** How many times an admin has manually retried this document. Never
   *  reset across cycles — see PropertiesTab.tsx's escalation threshold. */
  retryAttemptCount?: number;
  createdAt: number;
  source?: 'seller' | 'buyer';
}

/**
 * A document upload currently moving through the AI pipeline (uploaded but
 * not yet finalized into an InspectionReport). Surfaced so the user can see
 * what's in flight rather than thinking their upload disappeared.
 */
export interface AdminProcessingDocument {
  id: string;
  documentId?: string;
  name: string;
  documentType?: string;
  addressCheckStatus?: string;
  createdAt: number;
  source?: 'seller' | 'buyer';
}

/**
 * A completed NHD (hazard disclosure) document. NHD never produces an
 * InspectionReport (see backend getAllProperties.ts), so it can't appear in
 * `inspectionReports` like other finished document types — this is its own
 * completion signal, carrying `completedAt` as the date to display.
 */
export interface AdminCompletedNhdDocument {
  id: string;
  documentId?: string;
  name: string;
  documentType?: string;
  createdAt: number;
  source?: 'seller' | 'buyer';
  /** Unix seconds when NHD extraction finished. */
  completedAt: number;
}

/**
 * A completed HOA demand statement document. Like NHD, HOA_DEMAND never
 * produces an InspectionReport (see backend getAllProperties.ts), so it
 * gets its own completion signal, carrying `completedAt` as the date to
 * display.
 */
export interface AdminCompletedHoaDocument {
  id: string;
  documentId?: string;
  name: string;
  documentType?: string;
  createdAt: number;
  source?: 'seller' | 'buyer';
  /** Unix seconds when HOA demand extraction finished. */
  completedAt: number;
}

export interface AdminProperty {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country?: string;
  createdAt: number;
  updatedAt: number;
  owner: AdminPropertyOwner | null;
  /** Advisor / admin who created this Property record. Distinct from owner —
   *  owner is the homeowner relation (UserPropertyRelation), creator is who
   *  initiated the property add. Null on legacy rows that pre-date the
   *  createdBy field. */
  creator: AdminPropertyOwner | null;
  // Optional coordinates for map view
  latitude?: number;
  longitude?: number;
  inspectionReportCount: number;
  inspectionReports?: AdminInspectionReport[];
  /** Uploads halted at address verification (NEEDS_CONFIRMATION). */
  pendingDocuments?: AdminPendingDocument[];
  /** Uploads stalled on a Pulse-dependent step after exhausting retries. */
  incompleteDocuments?: AdminIncompleteDocument[];
  /** Uploads currently moving through the AI pipeline (post-upload, pre-extraction). */
  processingDocuments?: AdminProcessingDocument[];
  /** Finished NHD (hazard disclosure) documents — never in inspectionReports since NHD produces no InspectionReport. */
  completedNhdDocuments?: AdminCompletedNhdDocument[];
  /** Whether this property is HOA-governed. Manually toggled, or set automatically
   *  the first time a document classifies as HOA_DEMAND for this property. */
  hasHOA?: boolean;
  /** Finished HOA demand statement documents — never in inspectionReports since HOA_DEMAND produces no InspectionReport. */
  completedHoaDocuments?: AdminCompletedHoaDocument[];
  /** Count of distinct deals (across all advisors) with a listing on the property. */
  interestCount?: number;
  /** List price from the stored Property entity (MLS / manual / estimated). */
  price?: number;
  /** MLS listing date (unix seconds). Not yet plumbed from upstream — treat as optional. */
  listedDate?: number;
  /** Cross-group deal status ('Under Contract' | 'Pending' | 'Closed') if any advisor's group
   *  has this property at that stage. Undefined means no active advanced status. */
  dealStatus?: string;
  conditionHeadline?: string;
  conditionBlurb?: string;
  /** HauserEQ evaluation status, fetched server-to-server (no per-row
   * client call) via the /service/evaluations endpoint — see hauser-eq's
   * DLQ reprocessor (#16/#20) and Step Functions routing (#17) for what
   * populates contextStatus/conditionStatus. Undefined when the property
   * has never been evaluated, or when the HauserEQ lookup itself failed
   * (non-fatal — never blocks the properties list). */
  hauserEq?: {
    status?: string;
    contextStatus?: string;
    conditionStatus?: string;
    contextError?: Record<string, unknown> | string;
    conditionError?: Record<string, unknown> | string;
  };
}

/**
 * Converts an AdminProperty to a UserProperty shape suitable for use as
 * selectedProperty in PropertiesProvider. Advisors/admins are given Owner
 * role so downstream permission checks work correctly.
 */
export const adminPropertyToUserProperty = (p: AdminProperty): UserProperty => ({
  property: {
    id: p.id,
    name: p.name,
    address: p.address,
    city: p.city,
    state: p.state,
    zipCode: p.zipCode,
    country: p.country,
    createdAt: p.createdAt,
    updatedAt: p.updatedAt,
    conditionHeadline: p.conditionHeadline,
    conditionBlurb: p.conditionBlurb,
  },
  isOwned: true,
  isFavorite: false,
});

/**
 * Delete an inspection report and all its extracted issues (Admin only)
 */
export const deleteReport = async (
  propertyId: string,
  reportId: string,
  documentRecordId?: string,
): Promise<void> => {
  const params = new URLSearchParams();
  if (documentRecordId) params.set('documentRecordId', documentRecordId);
  const query = params.size > 0 ? `?${params.toString()}` : '';
  const url = `/admin/properties/${encodeURIComponent(propertyId)}/reports/${encodeURIComponent(
    reportId,
  )}${query}`;

  logger.debug(`DELETE ${url}`);
  await apiClient.delete(url);
};

/**
 * Trigger a Home or Area score recalculation for a property (Admin only)
 */
export const triggerScoreRecalculation = async (
  propertyId: string,
  scoreType: 'home' | 'area',
): Promise<void> => {
  const url = `/admin/properties/${encodeURIComponent(propertyId)}/recalculate`;

  logger.debug(`POST ${url}`);
  await apiClient.post(url, { scoreType });
};

/**
 * Delete a property (Admin only)
 */
export const deleteProperty = async (propertyId: string): Promise<void> => {
  const url = `/admin/properties/${encodeURIComponent(propertyId)}`;

  logger.debug(`DELETE ${url}`);
  await apiClient.delete(url);
};

/**
 * Transfer property ownership to a buyer by email (Admin or Advisor).
 *
 * `documentRecordIds` is the explicit set of property documents to hand over
 * to the new owner. The transfer modal pre-checks all of the property's docs
 * but lets the advisor uncheck files they want to hold back. An empty array
 * is a valid request and means "transfer ownership only, no docs."
 */
export const transferProperty = async (
  propertyId: string,
  toUserEmail: string,
  documentRecordIds: string[],
): Promise<{
  message: string;
  propertyId: string;
  ownerId: string;
  documentsTransferred: number;
  documentsFailed: number;
  cascadeError?: boolean;
}> => {
  const url = `/properties/${encodeURIComponent(propertyId)}/transfer-ownership`;

  logger.debug(`POST ${url}`);
  const response = await apiClient.post<{
    message: string;
    propertyId: string;
    ownerId: string;
    documentsTransferred: number;
    documentsFailed: number;
    cascadeError?: boolean;
  }>(url, { buyerEmail: toUserEmail, documentRecordIds });

  return response.data;
};

/**
 * Add a Cognito role to a user without clearing existing roles.
 * Adding the SuperAdmin role requires the caller to be a SuperAdmin.
 */
export const addUserRole = async (
  username: string,
  role: 'Admin' | 'Advisor' | 'Buyer' | 'SuperAdmin',
): Promise<void> => {
  const url = `/admin/users/${encodeURIComponent(username)}/add-role`;

  logger.debug(`POST ${url}`);
  await apiClient.post(url, { role });
};

/**
 * Remove a Cognito role from a user (Admin only; SuperAdmin role requires SuperAdmin caller)
 */
export const removeUserRole = async (
  username: string,
  role: 'Admin' | 'Advisor' | 'Buyer' | 'SuperAdmin',
): Promise<void> => {
  const url = `/admin/users/${encodeURIComponent(username)}/remove-role`;

  logger.debug(`DELETE ${url}`);
  await apiClient.delete(url, { data: { role } });
};

// ============ SCORING CONFIG ============

export interface ScoringConfigResponse {
  config: Record<string, any> | null;
  version: number;
  contextRegistry: ContextRegistry | null;
  updatedBy?: string;
  updatedByEmail?: string;
  updatedAt?: string;
}

export interface ContextRegistrySource {
  path: string;
  fallback_path?: string;
  label?: string;
  distance_bands_miles?: number[];
  coverage_radius_miles?: number;
  target_count?: number;
  weight: number;
}

export interface ContextRegistrySubfactor {
  key: string;
  display_name: string;
  weight: number;
  transform: string;
  enabled: boolean;
  score_direction: 'higher_is_better';
  sources: ContextRegistrySource[];
}

export interface ContextRegistryCategory {
  key: string;
  name: string;
  description: string;
  subscore: 'risk_exposure' | 'location_quality';
  confidence: string;
  subfactors: ContextRegistrySubfactor[];
}

export interface ContextRegistry {
  version: string;
  categories: ContextRegistryCategory[];
}

export interface PutScoringConfigRequest {
  config: Record<string, any>;
  userEmail?: string;
  changes?: Array<{
    field: string;
    oldValue: any;
    newValue: any;
    scope: string;
  }>;
}

export interface PutScoringConfigResponse {
  version: number;
  updatedBy: string;
  updatedAt: string;
}

export interface AuditLogEntry {
  ts: string;
  user: string;
  field: string;
  old: any;
  new: any;
  scope: string;
  version?: number;
}

export interface AuditLogResponse {
  auditLog: AuditLogEntry[];
}

export const getScoringConfig = async (): Promise<ScoringConfigResponse> => {
  const url = '/admin/scoring-config';

  logger.debug(`GET ${url}`);
  const response = await apiClient.get<ScoringConfigResponse>(url);

  return response.data;
};

export const putScoringConfig = async (
  request: PutScoringConfigRequest,
): Promise<PutScoringConfigResponse> => {
  const url = '/admin/scoring-config';

  logger.debug(`PUT ${url}`);
  const response = await apiClient.put<PutScoringConfigResponse>(url, request);

  return response.data;
};

export const getScoringConfigAuditLog = async (limit = 100): Promise<AuditLogResponse> => {
  const url = `/admin/scoring-config/audit-log?limit=${limit}`;

  logger.debug(`GET ${url}`);
  const response = await apiClient.get<AuditLogResponse>(url);

  return response.data;
};

export interface RescoreAllResponse {
  queued: number;
  failed: number;
  total: number;
  scoreType: 'home' | 'area';
}

export const rescoreAllProperties = async (
  scoreType: 'home' | 'area',
): Promise<RescoreAllResponse> => {
  const url = '/admin/rescore-all';
  logger.debug(`POST ${url}`);
  const response = await apiClient.post<RescoreAllResponse>(url, { scoreType });
  return response.data;
};

// ============ STATS / KPI ENDPOINTS ============

export interface AdminStatsGrowthMonth {
  month: string; // "YYYY-MM"
  groups: number;
  deals: number;
}

export interface AdminStatsCompany {
  totalGroups: number;
  activeGroups: number;
  archivedGroups: number;
  totalDeals: number;
  dealsByStatus: Record<string, number>;
  closedDeals30d: number;
  closedDeals90d: number;
  growthByMonth: AdminStatsGrowthMonth[];
}

export interface AdminStatsAdvisor {
  advisorId: string;
  activeGroups: number;
  archivedGroups: number;
  totalDeals: number;
  dealsByStatus: Record<string, number>;
  avgResponseMs: number | null;
  avgResponseMsEarly: number | null;
  avgResponseMsLate: number | null;
  avgInitiationRatio: number | null;
  lastActivityAt: number | null;
  totalMessages: number;
  avgDaysToFirstDeal: number | null;
  stalledDeals: number;
  feeOverrideCount: number;
  taskCompletionRate: number | null;
}

export interface AdminStatsCompanyExtended extends AdminStatsCompany {
  groupsWithNoDeals: number;
  stalledDeals: number;
  silentChurnCount: number;
  silentChurnRate: number;
  coAdvisorCollabRate: number;
  feeOverrideRate: number;
  avgOfferUtilization: number | null;
  avgDealVelocityDays: number | null;
  certificationRate: number;
  velocityTrend: { month: string; avgDays: number | null }[];
}

export interface AdminStatsTasks {
  total: number;
  completed: number;
  completionRate: number | null;
  advisorCompletionRate: number | null;
  clientCompletionRate: number | null;
  avgCompletionDays: number | null;
  certifiedDeals: number;
  certificationRate: number;
}

export interface AdminStatsPropertyRequests {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  approvalRate: number | null;
  avgApprovalDays: number | null;
}

export interface AdminStatsPipeline {
  avgDealAgeDaysByStatus: Record<string, number>;
  avgDealVelocityDays: number | null;
  velocityTrend: { month: string; avgDays: number | null }[];
  avgOfferUtilization: number | null;
  dealsCreatedLast30d: number;
  dealsClosedLast30d: number;
  dealsClosedLast90d: number;
  stalledDeals: number;
  groupsWithNoDeals: number;
}

export interface AdminStatsResponse {
  company: AdminStatsCompanyExtended;
  advisors: AdminStatsAdvisor[];
  tasks: AdminStatsTasks;
  propertyRequests: AdminStatsPropertyRequests;
  pipeline: AdminStatsPipeline;
}

export const getAdminStats = async (): Promise<AdminStatsResponse> => {
  const url = '/admin/stats';
  logger.debug(`GET ${url}`);
  const response = await apiClient.get<AdminStatsResponse>(url);
  return response.data;
};

export interface AdminInfraInsightsServiceSpend {
  service: string;
  spend: number;
  pct: number;
}

export interface AdminInfraInsightsTimePoint {
  date?: string;
  month?: string;
  start?: string;
  spend: number;
}

export interface AdminInfraInsightsPosture {
  label: string;
  status: 'healthy' | 'warning' | 'danger' | 'unknown';
  value: string;
  detail: string;
}

export interface AdminInfraInsightsWatchItem {
  severity: 'healthy' | 'warning' | 'danger' | 'unknown';
  title: string;
  detail: string;
}

export interface AdminInfraInsightsResponse {
  generatedAt: string;
  account: {
    id: string | null;
    arn: string | null;
    region: string;
  };
  spend: {
    mtd: number;
    previousMonth: number;
    monthDeltaPct: number | null;
    sevenDay: number;
    sevenDayDeltaPct: number | null;
    monthly: AdminInfraInsightsTimePoint[];
    daily: AdminInfraInsightsTimePoint[];
    topServices: AdminInfraInsightsServiceSpend[];
  };
  posture: AdminInfraInsightsPosture[];
  watchlist: AdminInfraInsightsWatchItem[];
  /** Claude/Anthropic API spend, sourced separately from AWS Cost Explorer. */
  claudeSpend: {
    mtd: number;
    previousMonth: number;
    monthDeltaPct: number | null;
    monthly: AdminInfraInsightsTimePoint[];
    daily: AdminInfraInsightsTimePoint[];
    topModels: AdminInfraInsightsServiceSpend[];
  };
  /** Google Maps Platform spend, sourced from Google Cloud Billing export in BigQuery. */
  googleSpend: {
    mtd: number;
    previousMonth: number;
    monthDeltaPct: number | null;
    monthly: AdminInfraInsightsTimePoint[];
    daily: AdminInfraInsightsTimePoint[];
    topSkus: AdminInfraInsightsServiceSpend[];
  };
  rawAvailability: {
    costExplorer: boolean;
    ses: boolean;
    cognito: boolean;
    anthropic: boolean;
    google: boolean;
  };
  errors: string[];
}

export const getAdminInfraInsights = async (): Promise<AdminInfraInsightsResponse> => {
  const url = '/admin/infra-insights';
  logger.debug(`GET ${url}`);
  const response = await apiClient.get<AdminInfraInsightsResponse>(url);
  return response.data;
};

export interface AdminAcquisitionInsightsTotals {
  activeUsers: number;
  newUsers: number;
  sessions: number;
  views: number;
  engagedSessions: number;
  eventCount: number;
  engagementSeconds: number;
}

export interface AdminAcquisitionInsightsDailyPoint {
  date: string;
  activeUsers: number;
  sessions: number;
  views: number;
}

export interface AdminAcquisitionInsightsDeviceEngagementPoint {
  date: string;
  desktop: number;
  mobile: number;
}

export interface AdminAcquisitionInsightsRankedRow {
  label: string;
  value: number;
  secondary?: number;
}

export interface AdminAcquisitionInsightsGeoRow extends AdminAcquisitionInsightsRankedRow {
  city?: string;
  region?: string;
  country?: string;
}

export interface AdminAcquisitionInsightsResponse {
  generatedAt: string;
  ga4: {
    propertyId: string;
    period: {
      period: AdminAcquisitionInsightsPeriod;
      compare: AdminAcquisitionInsightsCompare;
      current: {
        startDate: string;
        endDate: string;
      };
      previous: {
        startDate: string;
        endDate: string;
      };
    };
    current: AdminAcquisitionInsightsTotals;
    previous: AdminAcquisitionInsightsTotals;
    deltas: {
      activeUsers: number | null;
      newUsers: number | null;
      sessions: number | null;
      views: number | null;
      engagedSessions: number | null;
      eventCount: number | null;
      engagementSeconds: number | null;
    };
    daily: AdminAcquisitionInsightsDailyPoint[];
    deviceEngagement: AdminAcquisitionInsightsDeviceEngagementPoint[];
    topSources: AdminAcquisitionInsightsRankedRow[];
    topPages: AdminAcquisitionInsightsRankedRow[];
    topEvents: AdminAcquisitionInsightsRankedRow[];
    topLocations: AdminAcquisitionInsightsGeoRow[];
  };
  rawAvailability: {
    ga4: boolean;
  };
  errors: string[];
}

export type AdminAcquisitionInsightsPeriod =
  | 'last_7'
  | 'last_30'
  | 'last_90'
  | 'mtd'
  | 'previous_month'
  | 'ytd'
  | 'custom';
export type AdminAcquisitionInsightsCompare =
  | 'previous_period'
  | 'previous_month'
  | 'previous_year';

export interface AdminAcquisitionInsightsParams {
  period?: AdminAcquisitionInsightsPeriod;
  compare?: AdminAcquisitionInsightsCompare;
  startDate?: string;
  endDate?: string;
}

export const getAdminAcquisitionInsights = async (
  params: AdminAcquisitionInsightsParams = {},
): Promise<AdminAcquisitionInsightsResponse> => {
  const searchParams = new URLSearchParams();
  if (params.period) searchParams.set('period', params.period);
  if (params.compare) searchParams.set('compare', params.compare);
  if (params.startDate) searchParams.set('startDate', params.startDate);
  if (params.endDate) searchParams.set('endDate', params.endDate);
  const query = searchParams.toString();
  const url = query ? `/admin/acquisition-insights?${query}` : '/admin/acquisition-insights';
  logger.debug(`GET ${url}`);
  const response = await apiClient.get<AdminAcquisitionInsightsResponse>(url);
  return response.data;
};

// ============ LLM CONTENT GENERATION ============

export interface GenerateCopyResponse {
  content: string;
  usage: { input_tokens: number; output_tokens: number };
}

export const generateCopy = async (prompt: string): Promise<GenerateCopyResponse> => {
  const url = '/admin/generate-copy';

  logger.debug(`POST ${url}`);
  const response = await apiClient.post<GenerateCopyResponse>(url, { prompt });

  return response.data;
};
